import type { ReactElement } from "react"
import IconAtm from "@onestore/hel/dist/components/atoms/IconAtm"
import { getCtaButtonFlatData } from "@gatsby-plugin-generic-page/helpers/getCtaElementFlatData"
import { SquidexComponentType } from "@gatsby-plugin-generic-page/types/componentType"
import type { ProductBox } from "@gatsby-plugin-generic-page/types/productBoxTypes"
import { typeMatches } from "@onestore-graphql"
import TickCircle from "@onestore-icons/tick-circle.svg"
import { usePriceTypeContext } from "~/context/PriceTypeContext"
import type { ProductBoxSlimHookProps } from "~/hooks/useProductBoxSlim"
import isEmpty from "~/lib/isEmpty"
import type { Forms } from "~/types/squidex"
import type { PeriodName } from "../../../../../lib/api/types"
import ProductBoxOuterFooter from "./ProductBoxOuterFooter"
import ProductBoxWithCta from "./ProductBoxWithCta"
import ProductBoxWithCtaAnchor from "./ProductBoxWithCtaAnchor"
import ProductBoxWithCtaForm from "./ProductBoxWithCtaForm"
import ProductBoxWithSaleConfiguration from "./ProductBoxWithSaleConfiguration"
import productBoxContent from "./productBoxContent"

export type ProductBoxSlimProps = {
  productBox: ProductBox
  period?: PeriodName
  index?: number
  hasAnyOuterFooter?: boolean
  hasUpsellDrawer?: boolean
}

export type ProductType = Omit<ProductBoxSlimHookProps, "plan">

export default function ProductBoxSlim({
  productBox,
  period,
  index,
  hasAnyOuterFooter,
  hasUpsellDrawer,
}: ProductBoxSlimProps): ReactElement<ProductBoxSlimProps> {
  const {
    buttons,
    label,
    labels,
    parameters,
    descriptionText,
    outerFooter,
    title,
    isTrialBox,
  } = productBox.flatData

  const {
    saleConfiguration,
    bundle,
    cta,
    type,
    staticPrices,
    concept,
    variant,
    promoText,
    hideRenewalPrice,
    additionalText,
    planInputLabel,
    changePeriodModal,
  } = buttons[0]

  const content = productBoxContent(
    parameters,
    type,
    bundle,
    isTrialBox,
    <IconAtm icon={TickCircle} sizeSpace={2} color="success" />
  )

  const { currentPriceType } = usePriceTypeContext()

  const product: ProductType = {
    promoText:
      concept === "brand" && variant === "primary" ? promoText : undefined,
    concept,
    variant,
    discountLabel: !isTrialBox ? label : undefined,
    labels: !isEmpty(labels) ? labels : undefined,
    content,
    title: title || "",
    description: descriptionText,
    period,
    hideRenewalPrices: !isTrialBox ? hideRenewalPrice : undefined,
    additionalText: !isTrialBox ? additionalText : undefined,
    outerFooter: !isEmpty(outerFooter) && (
      <ProductBoxOuterFooter outerFooter={outerFooter} />
    ),
    buttonText: productBox.flatData.buttonText,
    backgroundImage: productBox.flatData.background,
  }

  if (typeMatches(type, "plan") && !isEmpty(saleConfiguration)) {
    if (isEmpty(saleConfiguration[0].flatData.defaultCloudBluePlan)) {
      return (
        <ProductBoxWithCta
          product={product}
          cta={cta}
          staticPrices={staticPrices}
          isTrialBox={isTrialBox}
          isDisabled
        />
      )
    }

    return (
      <ProductBoxWithSaleConfiguration
        product={product}
        bundle={bundle}
        saleConfiguration={saleConfiguration}
        planInputLabel={planInputLabel}
        changePeriodModal={
          !isEmpty(changePeriodModal)
            ? changePeriodModal[0].flatData
            : undefined
        }
        index={index}
        isTrialBox={isTrialBox}
        // Falls back to this box's own footer when no caller supplies
        // section-level coordination — standalone usage of ProductBoxSlim
        // outside a section that computes hasAnyOuterFooter across all
        // siblings will not have its footer row aligned with siblings.
        hasAnyOuterFooter={hasAnyOuterFooter ?? !isEmpty(outerFooter)}
        hasUpsellDrawer={hasUpsellDrawer}
      />
    )
  }

  if (typeMatches(type, "link") && !isEmpty(cta)) {
    const { actionTypeName, anchorId, form } = getCtaButtonFlatData(
      cta[0],
      currentPriceType
    )

    if (
      actionTypeName &&
      typeMatches(actionTypeName, SquidexComponentType.OPEN_FORM) &&
      !isEmpty(form)
    ) {
      return (
        <ProductBoxWithCtaForm
          product={product}
          form={form as Forms[]}
          cta={cta}
          staticPrices={staticPrices}
        />
      )
    }

    if (
      actionTypeName &&
      typeMatches(actionTypeName, SquidexComponentType.SCROLL_TO_SECTION) &&
      !isEmpty(anchorId)
    ) {
      return (
        <ProductBoxWithCtaAnchor
          product={product}
          anchorId={anchorId}
          cta={cta}
          staticPrices={staticPrices}
        />
      )
    }
  }

  return (
    <ProductBoxWithCta
      product={product}
      cta={cta}
      staticPrices={staticPrices}
      isTrialBox={isTrialBox}
    />
  )
}
